import { Controller } from "@hotwired/stimulus"
import { editorStore } from "../editor/store"
import { DEFAULT_SATELLITE_PROVIDER } from "../editor/satellite_providers"

// Tiny controller that mirrors the "show edge dimensions" checkbox with
// editorStore.ui.showEdgeDimensions. Lives in its own file (instead of
// inside a bigger properties controller) so desktop aside and mobile
// menu-sheet can share the same partial without pulling in unrelated
// behaviour.
export default class extends Controller {
  static targets = [
    "edgesCheckbox",
    "satelliteCheckbox",
    "satelliteProviderWrap",
    "satelliteProviderSelect",
    "satelliteOpacityWrap",
    "satelliteOpacitySlider",
  ]

  connect() {
    this.#sync()
    this._onUi = () => this.#sync()
    this._onPlot = () => this.#sync()
    editorStore.addEventListener("ui:change", this._onUi)
    editorStore.addEventListener("plot:change", this._onPlot)
  }

  disconnect() {
    editorStore.removeEventListener("ui:change", this._onUi)
    editorStore.removeEventListener("plot:change", this._onPlot)
  }

  toggleEdges(e) {
    editorStore.setUiShowEdgeDimensions(e.target.checked)
  }

  toggleSatellite(e) {
    editorStore.setUiSatelliteBgEnabled(e.target.checked)
  }

  // Slider step is whole percent; convert to 0..1 for the store.
  changeSatelliteOpacity(e) {
    const pct = Number(e.target.value)
    if (!Number.isFinite(pct)) return
    editorStore.setUiSatelliteBgOpacity(pct / 100)
  }

  changeSatelliteProvider(e) {
    editorStore.setUiSatelliteBgProvider(e.target.value)
  }

  #sync() {
    if (this.hasEdgesCheckboxTarget) {
      const want = editorStore.ui?.showEdgeDimensions !== false
      if (this.edgesCheckboxTarget.checked !== want) this.edgesCheckboxTarget.checked = want
    }
    // Satellite backdrop needs a geo-anchored plot. The demo plot and
    // pre-geo manual plots have no originLngLat, so the toggle would do
    // nothing — disable it and explain why instead of letting the user
    // check a box that silently no-ops.
    const satAvailable = !!editorStore.plot?.originLngLat
    const sat = editorStore.ui?.satelliteBg
    if (sat && this.hasSatelliteCheckboxTarget) {
      const cb = this.satelliteCheckboxTarget
      if (cb.checked !== sat.enabled) cb.checked = sat.enabled
      cb.disabled = !satAvailable
      cb.title = satAvailable ? "" : "Доступно после загрузки участка по кадастру или через «Показать на карте»"
    }
    // Source picker + opacity slider share the same visibility rule as the
    // toggle: only meaningful when the backdrop is actually on AND applicable.
    const satActive = !!(sat?.enabled && satAvailable)
    if (sat && this.hasSatelliteProviderWrapTarget) {
      this.satelliteProviderWrapTarget.classList.toggle("hidden", !satActive)
    }
    if (sat && this.hasSatelliteProviderSelectTarget) {
      const provider = sat.provider ?? DEFAULT_SATELLITE_PROVIDER
      if (this.satelliteProviderSelectTarget.value !== provider) {
        this.satelliteProviderSelectTarget.value = provider
      }
    }
    if (sat && this.hasSatelliteOpacityWrapTarget) {
      // Slider visible only when the feature is actually on AND applicable.
      this.satelliteOpacityWrapTarget.classList.toggle("hidden", !satActive)
    }
    if (sat && this.hasSatelliteOpacitySliderTarget) {
      const pct = Math.round((sat.opacity ?? 0.9) * 100)
      if (Number(this.satelliteOpacitySliderTarget.value) !== pct) {
        this.satelliteOpacitySliderTarget.value = String(pct)
      }
    }
  }
}
